const CONTEXT_COPY = {
  'chat-stream': {
    title: 'Response interrupted',
    message: 'I couldn’t get a response right now. Please try again.',
  },
  'chat-history': {
    title: 'Couldn’t load chats',
    message: 'We couldn’t reach your chat history. Please try again.',
  },
  'thread-load': {
    title: 'Conversation unavailable',
    message: 'We couldn’t load that conversation. Please try again.',
  },
  transcription: {
    title: 'Couldn’t transcribe audio',
    message: 'We couldn’t transcribe that recording. Please try again.',
  },
  'dashboard-load': {
    title: 'Couldn’t load dashboard',
    message: 'We couldn’t load your dashboard right now. Please try again.',
  },
  'dashboard-ai': {
    title: 'Dashboard answer unavailable',
    message: 'I couldn’t answer from your dashboard right now. Please try again.',
  },
  'plaid-load': {
    title: 'Couldn’t load connected accounts',
    message: 'We couldn’t load your connected accounts. Please try again.',
  },
  'plaid-link': {
    title: 'Couldn’t open secure connection',
    message: 'We couldn’t open the secure connection flow. Please try again, or come back in a moment.',
  },
  'plaid-sync': {
    title: 'Couldn’t refresh accounts',
    message: 'We couldn’t refresh your connected accounts. Please try again.',
  },
  'billing-load': {
    title: 'Couldn’t load billing',
    message: 'We couldn’t load your billing information. Please try again.',
  },
  'billing-action': {
    title: 'Billing change unavailable',
    message: 'We couldn’t update your billing settings. Please try again.',
  },
  'payment-method': {
    title: 'Payment method unavailable',
    message: 'We couldn’t update your payment method right now. Please try again.',
  },
  'billing-setup': {
    title: 'Checkout unavailable',
    message: 'We couldn’t complete checkout right now. Please try again.',
  },
  'profile-load': {
    title: 'Couldn’t load profile',
    message: 'We couldn’t load your profile right now. Please try again.',
  },
  'profile-save': {
    title: 'Couldn’t save changes',
    message: 'We couldn’t save your changes. Your entries are still here.',
  },
  feedback: {
    title: 'Couldn’t send feedback',
    message: 'We couldn’t send your feedback. Please try again.',
  },
  activation: {
    title: 'Setup step unavailable',
    message: 'We couldn’t complete that setup step. Please try again.',
  },
  generic: {
    title: 'Something went wrong',
    message: 'Something went wrong. Please try again.',
  },
};

const RETRY_LABELS = {};

function readDetail(error) {
  const detail = error?.detail;
  if (!detail) return {};
  if (typeof detail === 'string') {
    try {
      return JSON.parse(detail);
    } catch (_) {
      return { message: detail };
    }
  }
  if (typeof detail === 'object') return detail;
  return {};
}

function getStatus(error) {
  return Number(error?.status || error?.statusCode || readDetail(error).status || 0) || null;
}

function getCode(error) {
  return error?.code || readDetail(error).code || null;
}

function getRawMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return String(error.message || readDetail(error).message || '');
}

function normalizedMessage(error) {
  return getRawMessage(error).toLowerCase();
}

function isNetworkError(error) {
  const message = normalizedMessage(error);
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network error') ||
    message.includes('request timed out') ||
    message.includes('timeout') ||
    message.includes('load failed')
  );
}

function getContextCopy(context) {
  return CONTEXT_COPY[context] || CONTEXT_COPY.generic;
}

function paymentProviderMessage(error) {
  const message = normalizedMessage(error);
  if (message.includes('card was declined') || message.includes('card_declined')) {
    return 'Your card was declined. Please try a different card.';
  }
  if (message.includes('incomplete')) {
    return 'Your card details look incomplete. Please check them and try again.';
  }
  if (message.includes('incorrect') || message.includes('invalid card')) {
    return 'Please check your card details and try again.';
  }
  return null;
}

export function isBillingGateError(error) {
  return getStatus(error) === 402;
}

export function isAuthExpiredError(error) {
  return getStatus(error) === 401;
}

export function getRetryLabel(context) {
  return RETRY_LABELS[context] || 'Try again';
}

export function getUserFacingError(error, context = 'generic') {
  const status = getStatus(error);
  const copy = getContextCopy(context);
  const rawCode = getCode(error);

  if (status === 401) {
    return {
      title: 'Session expired',
      message: 'Please sign in again to keep using Wealth-Y.',
      actionLabel: null,
      severity: 'warning',
      recoverable: false,
      status,
      code: rawCode,
    };
  }

  if (status === 402) {
    return {
      title: 'Plan needs attention',
      message: 'Your plan needs attention before you can continue.',
      actionLabel: null,
      severity: 'warning',
      recoverable: false,
      status,
      code: rawCode,
    };
  }

  if (status === 403) {
    return {
      title: 'Access unavailable',
      message: 'You do not have access to do that.',
      actionLabel: null,
      severity: 'warning',
      recoverable: false,
      status,
      code: rawCode,
    };
  }

  if (status === 404) {
    return {
      ...copy,
      actionLabel: getRetryLabel(context),
      severity: 'error',
      recoverable: true,
      status,
      code: rawCode,
    };
  }

  if (status === 409 || status === 422) {
    return {
      title: 'Check your entries',
      message: 'Please check the highlighted fields and try again.',
      actionLabel: null,
      severity: 'warning',
      recoverable: true,
      status,
      code: rawCode,
    };
  }

  if (status === 429) {
    return {
      title: 'Please wait a moment',
      message: 'Too many attempts. Please wait a bit and try again.',
      actionLabel: getRetryLabel(context),
      severity: 'warning',
      recoverable: true,
      status,
      code: rawCode,
    };
  }

  if (context === 'payment-method' || context === 'billing-setup') {
    const paymentMessage = paymentProviderMessage(error);
    if (paymentMessage) {
      return {
        title: 'Check your card',
        message: paymentMessage,
        actionLabel: null,
        severity: 'warning',
        recoverable: true,
        status,
        code: rawCode,
      };
    }
  }

  if (isNetworkError(error)) {
    return {
      title: 'Connection interrupted',
      message: 'We couldn’t connect to Wealth-Y. Check your connection and try again.',
      actionLabel: getRetryLabel(context),
      severity: 'error',
      recoverable: true,
      status,
      code: rawCode,
    };
  }

  return {
    ...copy,
    actionLabel: getRetryLabel(context),
    severity: 'error',
    recoverable: true,
    status,
    code: rawCode,
  };
}

export function getErrorMessage(error, context = 'generic') {
  return getUserFacingError(error, context).message;
}
